import React, { useState, useEffect } from 'react';
import { fetchBlogPosts, deleteBlog, addBlog, updateBlog } from '../../services/dataService';
import { auth } from '../../services/firebase';
import BlogForm from './BlogForm';
import './AdminBlogs.css';

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fetchBlogsData = async () => {
    try {
      const blogsData = await fetchBlogPosts();
      console.log('Fetched blogs:', blogsData);
      setBlogs(blogsData);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogsData();
  }, []);

  const handleAddBlog = () => {
    setEditingBlog(null);
    setShowForm(true);
  };

  const handleEditBlog = (blog) => {
    setEditingBlog(blog);
    setShowForm(true);
  };

  const handleDeleteBlog = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
      try {
        console.log('=== DELETE BLOG DEBUG ===');
        console.log('Blog ID:', blogId);
        console.log('Current auth state:', auth.currentUser);
        
        // Test authentication first
        if (!auth.currentUser) {
          alert('ERROR: You are not logged in. Please refresh the page and log in again.');
          return;
        }
        
        console.log('User email:', auth.currentUser.email);
        console.log('User ID:', auth.currentUser.uid);
        
        await deleteBlog(blogId);
        setBlogs(blogs.filter(b => b.id !== blogId));
        alert('Blog post deleted successfully!');
      } catch (error) {
        console.error('=== DELETE ERROR DETAILS ===');
        console.error('Error object:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        
        const errorMessage = error.message || 'Error deleting blog post. Please try again.';
        alert(`ERROR: ${errorMessage}\n\nCheck browser console (F12) for more details.`);
      }
    }
  };

  const handleFormSubmit = async (blogData) => {
    try {
      if (editingBlog) {
        // Update existing blog
        await updateBlog(editingBlog.id, {
          ...blogData,
          updatedAt: new Date()
        });
        setBlogs(blogs.map(b => 
          b.id === editingBlog.id ? { ...b, ...blogData } : b
        ));
        alert('Blog post updated successfully!');
      } else {
        // Add new blog
        const blogId = await addBlog({
          ...blogData,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        setBlogs([...blogs, { id: blogId, ...blogData }]);
        alert('Blog post added successfully!');
      }
      setShowForm(false);
      setEditingBlog(null);
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Error saving blog post. Please try again.');
    }
  };

  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || blog.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = [
    { id: 'all', name: 'All Categories' },
    { id: 'gardening-tips', name: 'Gardening Tips' },
    { id: 'fertilizers', name: 'Fertilizers' },
    { id: 'seasonal', name: 'Seasonal Guide' },
    { id: 'composting', name: 'Composting' },
    { id: 'plant-care', name: 'Plant Care' },
    { id: 'container-gardening', name: 'Container Gardening' }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading blogs...</p>
      </div>
    );
  }

  if (showForm) {
    return (
      <BlogForm
        blog={editingBlog}
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setShowForm(false);
          setEditingBlog(null);
        }}
      />
    );
  }

  return (
    <div className="admin-blogs">
      <div className="admin-header">
        <div className="header-content">
          <h2>Blog Management</h2>
          <button className="btn btn-primary" onClick={handleAddBlog}>
            + Add New Blog Post
          </button>
        </div>
        
        <div className="admin-filters">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search blog posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={`blogs-grid ${filteredBlogs.length <= 2 ? 'few-items' : ''}`}>
        {filteredBlogs.length > 0 ? (
          filteredBlogs.map(blog => (
            <div key={blog.id} className="blog-card">
              <div className="blog-image">
                <img src={blog.image || '/logo.png'} alt={blog.title} />
                <div className="blog-category">{blog.category}</div>
              </div>
              
              <div className="blog-content">
                <h3>{blog.title}</h3>
                <p>{blog.excerpt}</p>
                
                <div className="blog-meta">
                  <span className="blog-date">{formatDate(blog.publishedAt)}</span>
                  <span className="blog-read-time">{blog.readTime}</span>
                </div>
                
                <div className="blog-stats">
                  <span>👁️ {blog.views || 0} views</span>
                  <span>❤️ {blog.likes || 0} likes</span>
                </div>
                
                <div className="blog-tags">
                  {blog.tags && blog.tags.slice(0, 3).map(tag => (
                    <span key={tag} className="tag">#{tag}</span>
                  ))}
                </div>
                
                <div className="blog-actions">
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleEditBlog(blog)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteBlog(blog.id)}
                    style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-blogs">
            <h3>No blog posts found</h3>
            <p>Try adjusting your search criteria or add a new blog post.</p>
            <button className="btn btn-primary" onClick={handleAddBlog}>
              Add First Blog Post
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBlogs;
